/**
 * Phase D D.11.2 + D.11.3 — Editeur CRC 16 sections avec navigation laterale.
 *
 * - Navigation laterale par section (Q2 = B)
 * - Auto pre-fill a la creation + bouton "Pre-remplir auto" (Q3 = C)
 * - Reformulation IA via ReformulableTextarea (4 section_types)
 * - Save whitelist 18 champs via PUT /chantiers/{cid}/crc/{crc_id}
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  X, FloppyDisk, Sparkle,
  IdentificationCard, Users, Calendar, ChartBar, Wrench, Lightbulb,
  Warning, PenNib, Question, ShieldCheck, FileText, CurrencyEur,
  ListChecks, NotePencil, Plus, Trash,
} from '@phosphor-icons/react';
import ReformulableTextarea from '../../shared/ReformulableTextarea';
import { crcStatut, crcEstVerrouille, CRC_STATUT_LABELS } from './crcStatut';
import { titreCompteRendu } from '../../../lib/libelles';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { id: 'entete', label: 'En-tête', icon: IdentificationCard, auto: true },
  { id: 'intervenants', label: 'Intervenants', icon: Users, auto: true },
  { id: 'presence', label: 'Feuille de présence', icon: Users },
  { id: 'rappel', label: 'Rappel CR précédent', icon: NotePencil, auto: true },
  { id: 'avancement_semaine', label: "État d'avancement", icon: ChartBar },
  { id: 'planning', label: 'Avancement planning', icon: Calendar, auto: true },
  { id: 'constats', label: 'Constats / Travaux', icon: Wrench },
  { id: 'decisions', label: 'Décisions', icon: Lightbulb },
  { id: 'problematiques', label: '⭐ Problématiques', icon: Warning },
  { id: 'espace_libre', label: '⭐ Espace libre archi', icon: PenNib },
  { id: 'coordination', label: 'Coordination technique', icon: Question },
  { id: 'securite', label: 'Sécurité chantier', icon: ShieldCheck },
  { id: 'demarches', label: 'Démarches admin', icon: FileText },
  { id: 'finances', label: 'Situation financière', icon: CurrencyEur, auto: true },
  { id: 'prochaines', label: 'Prochaines étapes', icon: Calendar },
  { id: 'actions', label: '⭐ Tableau suivi actions', icon: ListChecks },
];

// Whitelist update fields (alignee avec backend _ALLOWED_CRC_UPDATE_FIELDS)
const EDITABLE_PAYLOAD_KEYS = [
  'title', 'date', 'location', 'attendees', 'notes',
  'synthese_semaine', 'constats', 'constats_par_zone', 'decisions',
  'problematiques', 'espace_libre_brut', 'espace_libre_reformule',
  'coord_bet', 'coord_sous_traitants', 'securite_points',
  'demarches_admin', 'prochaines_etapes', 'convocations',
  'prochain_cr_date', 'objet',
];

export default function CRCSectionEditor({ chantierId, crcId, onClose }) {
  const [activeSection, setActiveSection] = useState('avancement_semaine');
  const [crc, setCrc] = useState(null);
  // Champs modifiés depuis le dernier chargement : seuls ceux-là sont envoyés
  // (un champ non touché n'est jamais réécrit).
  const [changedKeys, setChangedKeys] = useState([]);
  const dirty = changedKeys.length > 0;
  const [saving, setSaving] = useState(false);
  const [preFilling, setPreFilling] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadCrc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crcId]);

  // CHA-03 — GET /chantiers/{cid}/crc/{id} renvoie le CRC complet (16 sections).
  // GET /meetings/{cid} n'en renvoie aucune : l'éditeur rouvrait vide et
  // l'enregistrement écrasait les listes existantes (problématiques, etc.).
  const loadCrc = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/chantiers/${chantierId}/crc/${crcId}`, {
        withCredentials: true,
      });
      setCrc(res.data);
      setChangedKeys([]);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur de chargement du compte rendu');
    }
  };

  // CHA-11 — CR diffusé : lecture seule (le backend refuse aussi, 423)
  const locked = crcEstVerrouille(crc);

  const updateField = (key, value) => {
    if (locked) return;
    setCrc((prev) => ({ ...prev, [key]: value }));
    setChangedKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
  };

  const handleSave = async () => {
    if (!dirty || !crc || locked) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {};
      for (const k of EDITABLE_PAYLOAD_KEYS) {
        if (changedKeys.includes(k) && crc[k] !== undefined) payload[k] = crc[k];
      }
      await axios.put(
        `${API_URL}/api/chantiers/${chantierId}/crc/${crcId}`,
        payload,
        { withCredentials: true },
      );
      setChangedKeys([]);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handlePreFill = async () => {
    if (dirty) {
      setError('Enregistrez vos modifications avant de pré-remplir.');
      return;
    }
    if (!window.confirm('Pré-remplir auto depuis intervenants/situations/photos/points ? Les champs déjà remplis ne seront pas écrasés.')) {
      return;
    }
    setPreFilling(true);
    setError(null);
    try {
      await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/crc/${crcId}/pre-fill`,
        {},
        { withCredentials: true },
      );
      await loadCrc();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur pré-remplissage');
    } finally {
      setPreFilling(false);
    }
  };

  const handleExportWordV2 = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/chantiers/${chantierId}/crc/${crcId}/export-docx-v2`,
        { responseType: 'blob', withCredentials: true },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `CR_n${crc?.numero || '?'}_v2.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur export Word v2');
    }
  };

  if (!crc) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
        <div className="bg-white rounded-xl p-8">
          {error ? <p className="text-red-600">{error}</p> : <p>Chargement…</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-7xl h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between bg-gray-50">
          <div>
            <h2 className="text-xl font-bold">
              CR n°{crc.numero || '?'} — {crc.title ? titreCompteRendu(crc.title) : 'Sans titre'}
            </h2>
            <p className="text-sm text-gray-500">
              {CRC_STATUT_LABELS[crcStatut(crc)] || 'Brouillon'}
              {locked && ' 🔒'}
              {crc.imported_externe && ' • 📥 Importé externe'}
              {crc.pdf_final_filename && ' • ✅ PDF final'}
              {dirty && ' • Modifications non sauvegardées'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePreFill}
              disabled={preFilling || locked}
              className="px-3 py-1.5 bg-purple-50 text-purple-700 border border-purple-200 rounded-lg flex items-center gap-1.5 hover:bg-purple-100 disabled:opacity-50 text-sm"
              data-testid="crc-editor-prefill"
            >
              <Sparkle size={16} />
              {preFilling ? 'Pré-remplissage…' : 'Pré-remplir auto'}
            </button>
            <button
              onClick={handleExportWordV2}
              className="px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-sm hover:bg-blue-100"
              data-testid="crc-editor-export-v2"
            >
              📄 Exporter Word v2
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !dirty || locked}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg flex items-center gap-1.5 hover:bg-blue-700 disabled:opacity-50 text-sm"
              data-testid="crc-editor-save"
            >
              <FloppyDisk size={16} />
              {saving ? 'Sauvegarde…' : 'Sauvegarder'}
            </button>
            <button onClick={onClose} className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded-lg text-sm">
              <X size={18} />
            </button>
          </div>
        </div>

        {error && (
          <div className="px-6 py-2 bg-red-50 border-b border-red-200 text-sm text-red-700">{error}</div>
        )}
        {locked && (
          <div className="px-6 py-2 bg-gray-50 border-b text-sm text-gray-600" data-testid="crc-editor-locked">
            🔒 Ce compte rendu a été diffusé : il est en lecture seule. Consignez les corrections dans le compte rendu suivant.
          </div>
        )}

        {/* Body : nav + edition */}
        <div className="flex-1 flex overflow-hidden">
          <nav className="w-64 border-r overflow-y-auto bg-gray-50 py-2">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => setActiveSection(s.id)}
                className={`w-full px-4 py-2 text-left flex items-center gap-2 text-sm hover:bg-gray-100 ${
                  activeSection === s.id
                    ? 'bg-blue-50 text-blue-700 font-medium border-r-2 border-blue-600'
                    : 'text-gray-700'
                }`}
                data-testid={`crc-section-${s.id}`}
              >
                <s.icon size={18} />
                <span className="flex-1">{s.label}</span>
                {s.auto && <span className="text-xs text-gray-400">(auto)</span>}
              </button>
            ))}
          </nav>

          <div className="flex-1 overflow-y-auto p-6">
            {renderSection(activeSection, crc, updateField, chantierId, crcId)}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Rendering helper + sections
// ============================================================

function renderSection(id, crc, onChange, chantierId, crcId) {
  switch (id) {
    case 'entete':
      return <EnteteSection crc={crc} onChange={onChange} />;
    case 'intervenants':
      return <IntervenantsSection chantierId={chantierId} />;
    case 'presence':
      return <PresenceSection crc={crc} onChange={onChange} />;
    case 'rappel':
      return <RappelSection chantierId={chantierId} crcId={crcId} />;
    case 'avancement_semaine':
      return <AvancementSection crc={crc} onChange={onChange} chantierId={chantierId} crcId={crcId} />;
    case 'planning':
      return <PlanningSection chantierId={chantierId} />;
    case 'constats':
      return <ConstatsSection crc={crc} onChange={onChange} chantierId={chantierId} crcId={crcId} />;
    case 'decisions':
      return <DecisionsSection crc={crc} onChange={onChange} />;
    case 'problematiques':
      return <ProblematiquesSection crc={crc} onChange={onChange} chantierId={chantierId} crcId={crcId} />;
    case 'espace_libre':
      return <EspaceLibreSection crc={crc} onChange={onChange} chantierId={chantierId} crcId={crcId} />;
    case 'coordination':
      return <CoordinationSection crc={crc} onChange={onChange} />;
    case 'securite':
      return <SecuriteSection crc={crc} onChange={onChange} />;
    case 'demarches':
      return <DemarchesSection crc={crc} onChange={onChange} />;
    case 'finances':
      return <FinancesSection chantierId={chantierId} />;
    case 'prochaines':
      return <ProchainesSection crc={crc} onChange={onChange} />;
    case 'actions':
      return <ActionsSection chantierId={chantierId} crcId={crcId} />;
    default:
      return <p>Section inconnue</p>;
  }
}

function SectionTitle({ children }) {
  return <h3 className="text-lg font-bold mb-3">{children}</h3>;
}

function EnteteSection({ crc, onChange }) {
  return (
    <div className="space-y-4">
      <SectionTitle>En-tête</SectionTitle>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Objet du CR</label>
        <input
          type="text"
          value={crc.objet || ''}
          onChange={(e) => onChange('objet', e.target.value)}
          placeholder="Compte rendu de la réunion du …"
          className="w-full px-3 py-2 border rounded-lg text-sm"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Date du prochain CR</label>
        <input
          type="date"
          value={(crc.prochain_cr_date || '').slice(0, 10)}
          onChange={(e) => onChange('prochain_cr_date', e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm"
        />
      </div>
    </div>
  );
}

function IntervenantsSection({ chantierId }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API_URL}/api/chantiers/${chantierId}/intervenants`, { withCredentials: true })
      .then((r) => setList(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [chantierId]);
  return (
    <div className="space-y-3">
      <SectionTitle>Intervenants (auto depuis chantier)</SectionTitle>
      {loading && <p className="text-gray-500">Chargement…</p>}
      {!loading && list.length === 0 && (
        <p className="text-gray-500 italic">Aucun intervenant rattaché. Ajoutez-en via le menu chantier.</p>
      )}
      {!loading && list.length > 0 && (
        <table className="w-full text-sm border rounded-lg overflow-hidden">
          <thead className="bg-gray-50 text-xs">
            <tr><th className="p-2 text-left">Rôle</th><th className="p-2 text-left">Nom</th><th className="p-2 text-left">Société</th><th className="p-2 text-left">Email</th><th className="p-2 text-left">Téléphone</th></tr>
          </thead>
          <tbody>
            {list.map((i, idx) => (
              <tr key={i.user_id || idx} className="border-t">
                <td className="p-2">{i.role}</td>
                <td className="p-2">{`${i.prenom || ''} ${i.name || ''}`.trim()}</td>
                <td className="p-2">{i.societe || '—'}</td>
                <td className="p-2 text-xs">{i.email}</td>
                <td className="p-2 text-xs">{i.telephone || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function PresenceSection({ crc, onChange }) {
  const att = crc.attendees || [];
  return (
    <div className="space-y-3">
      <SectionTitle>Feuille de présence</SectionTitle>
      <p className="text-sm text-gray-600">
        Champ <code>attendees</code> backend (liste emails). Étendre vers struct détaillée Présent/Absent en V2.
      </p>
      <textarea
        value={Array.isArray(att) ? att.map((a) => (a && typeof a === 'object' ? (a.email || a.name || '') : a)).join('\n') : ''}
        onChange={(e) => onChange('attendees', e.target.value.split('\n').filter(Boolean))}
        rows={6}
        placeholder="1 email par ligne"
        className="w-full px-3 py-2 border rounded-lg text-sm"
      />
    </div>
  );
}

function RappelSection({ chantierId, crcId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API_URL}/api/chantiers/${chantierId}/since-last-crc`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => {});
  }, [chantierId, crcId]);
  return (
    <div className="space-y-3">
      <SectionTitle>Rappel CR précédent (auto)</SectionTitle>
      {!data && <p className="text-gray-500">Chargement…</p>}
      {data && (
        <div className="text-sm">
          {data.last_crc_numero ? (
            <p className="italic">
              Compte rendu n°{data.last_crc_numero} du {data.last_crc_date?.slice(0, 10)} ; sans observation reçue dans le délai de huit jours, le contenu en est réputé accepté.
            </p>
          ) : (
            <p className="text-gray-500">Premier compte rendu du chantier.</p>
          )}
          <div className="mt-3 p-2 bg-gray-50 rounded text-xs">
            <strong>Évolutions depuis dernier CR :</strong> {data.total} (messages: {data.details?.messages || 0}, réserves: {data.details?.reserves || 0}, factures: {data.details?.factures || 0}, actions levées: {data.details?.taches_terminees || 0})
          </div>
        </div>
      )}
    </div>
  );
}

function AvancementSection({ crc, onChange, chantierId, crcId }) {
  return (
    <div className="space-y-4">
      <SectionTitle>État d'avancement de la semaine</SectionTitle>
      <ReformulableTextarea
        label="Synthèse de la semaine"
        value={crc.synthese_semaine}
        onChange={(v) => onChange('synthese_semaine', v)}
        sectionType="synthese_semaine"
        rows={6}
        placeholder="La semaine a permis de finaliser…"
        chantierId={chantierId}
        crcId={crcId}
      />
    </div>
  );
}

function PlanningSection({ chantierId }) {
  const [synth, setSynth] = useState(null);
  useEffect(() => {
    axios.get(`${API_URL}/api/chantiers/${chantierId}/situations/synthese`, { withCredentials: true })
      .then((r) => setSynth(r.data))
      .catch(() => {});
  }, [chantierId]);
  return (
    <div className="space-y-3">
      <SectionTitle>Avancement planning (auto)</SectionTitle>
      {synth && (
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="border rounded p-3"><strong>Avancement :</strong> {synth.avancement_pct}%</div>
          <div className="border rounded p-3"><strong>Actions :</strong> {synth.nb_taches_completed}/{synth.nb_taches_total}</div>
        </div>
      )}
    </div>
  );
}

function ConstatsSection({ crc, onChange, chantierId, crcId }) {
  return (
    <div className="space-y-3">
      <SectionTitle>Constats / Travaux de la semaine</SectionTitle>
      <ReformulableTextarea
        label="Constats"
        value={crc.constats}
        onChange={(v) => onChange('constats', v)}
        sectionType="constat"
        rows={6}
        chantierId={chantierId}
        crcId={crcId}
      />
    </div>
  );
}

function DecisionsSection({ crc, onChange }) {
  return (
    <div className="space-y-3">
      <SectionTitle>Décisions techniques</SectionTitle>
      <textarea
        value={crc.decisions || ''}
        onChange={(e) => onChange('decisions', e.target.value)}
        rows={6}
        placeholder="Aucune décision actée."
        className="w-full px-3 py-2 border rounded-lg text-sm"
      />
    </div>
  );
}

function ProblematiquesSection({ crc, onChange, chantierId, crcId }) {
  const list = crc.problematiques || [];
  const updateOne = (id, k, v) => {
    onChange('problematiques', list.map((p) => (p.id === id ? { ...p, [k]: v } : p)));
  };
  const add = () => {
    const np = {
      id: `prob_${Date.now()}`,
      titre: 'Nouvelle problématique',
      description_brute: '',
      description_reformulee: '',
      impact: null,
      created_at: new Date().toISOString(),
    };
    onChange('problematiques', [...list, np]);
  };
  const remove = (id) => {
    if (!window.confirm('Supprimer cette problématique ?')) return;
    onChange('problematiques', list.filter((p) => p.id !== id));
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <SectionTitle>⭐ Problématiques rencontrées</SectionTitle>
        <button onClick={add} className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm flex items-center gap-1" data-testid="problematique-add">
          <Plus size={14} /> Ajouter
        </button>
      </div>
      {list.length === 0 && <p className="text-gray-500 italic">Aucune problématique cette semaine.</p>}
      {list.map((p) => (
        <div key={p.id} className="border rounded-lg p-4 space-y-3 bg-yellow-50/30">
          <div className="flex items-center justify-between gap-2">
            <input
              type="text"
              value={p.titre}
              onChange={(e) => updateOne(p.id, 'titre', e.target.value)}
              className="text-md font-semibold flex-1 border-b focus:outline-none focus:border-blue-500 bg-transparent"
              placeholder="Titre court"
            />
            <button onClick={() => remove(p.id)} className="text-red-500 hover:text-red-700">
              <Trash size={18} />
            </button>
          </div>
          <ReformulableTextarea
            label="Description (notes brutes)"
            value={p.description_brute}
            onChange={(v) => updateOne(p.id, 'description_brute', v)}
            sectionType="problematique"
            contexte={p.titre}
            chantierId={chantierId}
            crcId={crcId}
            placeholder="charpente pourrie au 4e droite, jour visible au chien-assis…"
          />
          <div>
            <label className="block text-xs text-gray-600 mb-1">Texte reformulé (auto-rempli par IA)</label>
            <textarea
              value={p.description_reformulee || ''}
              onChange={(e) => updateOne(p.id, 'description_reformulee', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border rounded-lg text-sm bg-blue-50"
            />
          </div>
          <select
            value={p.impact || ''}
            onChange={(e) => updateOne(p.id, 'impact', e.target.value || null)}
            className="px-3 py-1.5 border rounded text-sm"
          >
            <option value="">Impact (optionnel)</option>
            <option value="delais">Délais</option>
            <option value="couts">Coûts</option>
            <option value="securite">Sécurité</option>
            <option value="conformite">Conformité</option>
          </select>
        </div>
      ))}
    </div>
  );
}

function EspaceLibreSection({ crc, onChange, chantierId, crcId }) {
  return (
    <div className="space-y-4">
      <SectionTitle>⭐ Espace libre architecte</SectionTitle>
      <p className="text-sm text-gray-600">Notez librement vos observations. L'IA peut reformuler.</p>
      <ReformulableTextarea
        label="Notes brutes"
        value={crc.espace_libre_brut}
        onChange={(v) => onChange('espace_libre_brut', v)}
        sectionType="espace_libre"
        chantierId={chantierId}
        crcId={crcId}
        rows={6}
        placeholder="Ajoutez vos observations libres ici…"
      />
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Texte reformulé (auto-rempli)</label>
        <textarea
          value={crc.espace_libre_reformule || ''}
          onChange={(e) => onChange('espace_libre_reformule', e.target.value)}
          rows={6}
          className="w-full px-3 py-2 border rounded-lg text-sm bg-blue-50"
        />
      </div>
    </div>
  );
}

function CoordinationSection({ crc, onChange }) {
  return (
    <div className="space-y-4">
      <SectionTitle>Coordination technique</SectionTitle>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Bureau d'études (BET)</label>
        <textarea
          value={crc.coord_bet || ''}
          onChange={(e) => onChange('coord_bet', e.target.value)}
          rows={4}
          placeholder="Pas d'élément BET notable."
          className="w-full px-3 py-2 border rounded-lg text-sm"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Sous-traitants</label>
        <textarea
          value={crc.coord_sous_traitants || ''}
          onChange={(e) => onChange('coord_sous_traitants', e.target.value)}
          rows={4}
          className="w-full px-3 py-2 border rounded-lg text-sm"
        />
      </div>
    </div>
  );
}

function SecuriteSection({ crc, onChange }) {
  const list = crc.securite_points || [];
  const updateOne = (idx, k, v) => onChange('securite_points', list.map((p, i) => (i === idx ? { ...p, [k]: v } : p)));
  const add = () => onChange('securite_points', [...list, { niveau: 'Pour info', texte: '' }]);
  const remove = (idx) => onChange('securite_points', list.filter((_, i) => i !== idx));
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <SectionTitle>Sécurité chantier</SectionTitle>
        <button onClick={add} className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm flex items-center gap-1">
          <Plus size={14} /> Ajouter
        </button>
      </div>
      {list.length === 0 && <p className="text-gray-500 italic">Aucun point de vigilance majeur.</p>}
      {list.map((p, i) => (
        <div key={i} className="border rounded p-3 flex items-center gap-2">
          <select
            value={p.niveau || 'Pour info'}
            onChange={(e) => updateOne(i, 'niveau', e.target.value)}
            className="px-2 py-1 border rounded text-xs"
          >
            <option>URGENT</option>
            <option>Permanent</option>
            <option>Pour info</option>
          </select>
          <input
            type="text"
            value={p.texte || ''}
            onChange={(e) => updateOne(i, 'texte', e.target.value)}
            className="flex-1 px-2 py-1 border rounded text-sm"
            placeholder="Description du point sécurité"
          />
          <button onClick={() => remove(i)} className="text-red-500"><Trash size={16} /></button>
        </div>
      ))}
    </div>
  );
}

function ListEditor({ list, onChange, addLabel = 'Ajouter', placeholder = '' }) {
  return (
    <>
      {list.map((item, i) => (
        <div key={i} className="flex items-center gap-2 mb-2">
          <input
            type="text"
            value={item}
            onChange={(e) => onChange(list.map((v, idx) => (idx === i ? e.target.value : v)))}
            className="flex-1 px-3 py-1.5 border rounded text-sm"
            placeholder={placeholder}
          />
          <button onClick={() => onChange(list.filter((_, idx) => idx !== i))} className="text-red-500"><Trash size={16} /></button>
        </div>
      ))}
      <button onClick={() => onChange([...list, ''])} className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm flex items-center gap-1">
        <Plus size={14} /> {addLabel}
      </button>
    </>
  );
}

function DemarchesSection({ crc, onChange }) {
  return (
    <div className="space-y-3">
      <SectionTitle>Démarches administratives</SectionTitle>
      <ListEditor
        list={crc.demarches_admin || []}
        onChange={(v) => onChange('demarches_admin', v)}
        addLabel="Ajouter une démarche"
        placeholder="DP, attestation, autorisation…"
      />
    </div>
  );
}

function ProchainesSection({ crc, onChange }) {
  return (
    <div className="space-y-3">
      <SectionTitle>Prochaines étapes (semaine S+1)</SectionTitle>
      <ListEditor
        list={crc.prochaines_etapes || []}
        onChange={(v) => onChange('prochaines_etapes', v)}
        addLabel="Ajouter une étape"
        placeholder="Réception lot 5, livraison matériaux…"
      />
    </div>
  );
}

function FinancesSection({ chantierId }) {
  const [synth, setSynth] = useState(null);
  useEffect(() => {
    axios.get(`${API_URL}/api/chantiers/${chantierId}/situations/synthese`, { withCredentials: true })
      .then((r) => setSynth(r.data))
      .catch(() => {});
  }, [chantierId]);
  if (!synth) return <p className="text-gray-500">Chargement…</p>;
  return (
    <div className="space-y-4">
      <SectionTitle>Situation financière (auto via E.7)</SectionTitle>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="border rounded p-3"><strong>Marché HT :</strong> {synth.marche_ht?.toLocaleString('fr-FR')} €</div>
        <div className="border rounded p-3"><strong>Avancement :</strong> {synth.avancement_pct}%</div>
        <div className="border rounded p-3"><strong>Payé HT :</strong> {synth.factures_payees_ht?.toLocaleString('fr-FR')} €</div>
        <div className="border rounded p-3"><strong>Validé HT :</strong> {synth.factures_validees_ht?.toLocaleString('fr-FR')} €</div>
        <div className="border rounded p-3"><strong>RG en cours :</strong> {synth.rg_en_cours_ht?.toLocaleString('fr-FR')} € ({synth.rg_taux}%)</div>
        <div className="border rounded p-3"><strong>Reste à facturer :</strong> {synth.reste_a_facturer_ht?.toLocaleString('fr-FR')} €</div>
      </div>
      {synth.par_entreprise?.length > 0 && (
        <table className="w-full text-sm border rounded">
          <thead className="bg-gray-50 text-xs">
            <tr><th className="p-2 text-left">Entreprise</th><th className="p-2 text-right">Marché HT</th><th className="p-2 text-right">Payé HT</th><th className="p-2 text-right">Validé HT</th><th className="p-2 text-right">RG HT</th><th className="p-2 text-right">Reste HT</th></tr>
          </thead>
          <tbody>
            {synth.par_entreprise.map((e, i) => (
              <tr key={i} className="border-t">
                <td className="p-2">{e.entreprise}</td>
                <td className="p-2 text-right">{e.marche_ht?.toLocaleString('fr-FR')} €</td>
                <td className="p-2 text-right">{e.facture_paye_ht?.toLocaleString('fr-FR')} €</td>
                <td className="p-2 text-right">{e.facture_valide_ht?.toLocaleString('fr-FR')} €</td>
                <td className="p-2 text-right">{e.rg_ht?.toLocaleString('fr-FR')} €</td>
                <td className="p-2 text-right">{e.reste_ht?.toLocaleString('fr-FR')} €</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const STATUT_COLORS = {
  EN_ATTENTE: 'bg-gray-100 text-gray-700',
  EN_COURS: 'bg-orange-100 text-orange-700',
  A_VALIDER: 'bg-yellow-100 text-yellow-700',
  CLOTURE: 'bg-green-100 text-green-700',
  REPORTE: 'bg-purple-100 text-purple-700',
  CONTESTE: 'bg-red-100 text-red-700',
  REALISE: 'bg-green-100 text-green-700',
  URGENT: 'bg-red-100 text-red-700',
  A_FAIRE: 'bg-yellow-100 text-yellow-700',
};

function StatutBadge({ statut }) {
  const cls = STATUT_COLORS[statut] || 'bg-gray-100';
  return <span className={`px-2 py-0.5 rounded text-xs ${cls}`}>{statut}</span>;
}

function ActionsSection({ chantierId, crcId }) {
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API_URL}/api/chantiers/${chantierId}/crc/${crcId}/points`, { withCredentials: true })
      .then((r) => setPoints(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [chantierId, crcId]);
  const grouped = points.reduce((acc, p) => {
    const cat = p.categorie || p.lot || 'GÉNÉRALITÉS';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(p);
    return acc;
  }, {});
  if (loading) return <p className="text-gray-500">Chargement…</p>;
  return (
    <div className="space-y-4">
      <SectionTitle>⭐ Tableau de suivi des actions ({points.length})</SectionTitle>
      <p className="text-sm text-gray-600">Ajoutez ou modifiez les points depuis le détail du compte rendu (onglet Suivi).</p>
      {Object.entries(grouped).map(([cat, items]) => (
        <div key={cat} className="border rounded-lg overflow-hidden">
          <div className="bg-gray-100 px-3 py-2 font-medium text-sm">{cat} ({items.length})</div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs">
              <tr><th className="p-2 text-left">Date</th><th className="p-2 text-left">Libellé</th><th className="p-2 text-left">Responsable</th><th className="p-2 text-left">Statut</th></tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p._id || p.id} className="border-t">
                  <td className="p-2">{(p.date || p.created_at || '').slice(0, 10)}</td>
                  <td className="p-2">{p.description || p.title || p.libelle || '—'}</td>
                  <td className="p-2">{p.entreprise || p.responsable || '—'}</td>
                  <td className="p-2"><StatutBadge statut={p.statut} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      {points.length === 0 && <p className="text-gray-500 italic">Aucune action pour ce CR.</p>}
    </div>
  );
}
